import Permission from 'owa-service/lib/contract/Permission';
import PermissionLevelType from 'owa-service/lib/contract/PermissionLevelType';
import PERMISSION_CONSTANTS from '../utils/permissionConstants';

// Type definition for defining map objects where key is of PermissionLevelType and value is of Permission type (it will not have UserId property as that is not dependent on the permission level).
type PermissionLevelToPermissionMap = { [permissionLevelType in PermissionLevelType]: Permission };

// Map object of type PermissionLevelToPermissionMap. Permissions in the map will not have UserId property as that is not dependent on the permission level.
// For referencecls: https://msft.spoppe.com/teams/EXOinbox/_layouts/15/WopiFrame.aspx?sourcedoc={4d6614f9-f0ba-4d24-801a-371d17ed662c}&action=edit&wd=target%28Commercial%20%28legacy%5C%29%20Parity.one%7C6fa9a550-d77d-47fe-b327-a556c3dbb5a1%2FDev%20Design%7Cdae791d4-7a6d-4445-9761-70d77bd80b2b%2F%29
const permissionLevelToPermissionMap: PermissionLevelToPermissionMap = {
    Owner: {
        PermissionLevel: PERMISSION_CONSTANTS.PERMISSION_LEVEL_TYPE.OWNER,
        CanCreateItems: true,
        CanCreateSubFolders: true,
        IsFolderContact: true,
        IsFolderVisible: true,
        IsFolderOwner: true,
        EditItems: PERMISSION_CONSTANTS.EDIT_ACCESS.ALL,
        DeleteItems: PERMISSION_CONSTANTS.EDIT_ACCESS.ALL,
        ReadItems: PERMISSION_CONSTANTS.READ_ACCESS.FULL_DETAILS,
    },
    PublishingEditor: {
        PermissionLevel: PERMISSION_CONSTANTS.PERMISSION_LEVEL_TYPE.PUBLISHING_EDITOR,
        CanCreateItems: true,
        CanCreateSubFolders: true,
        IsFolderContact: false,
        IsFolderVisible: true,
        IsFolderOwner: false,
        EditItems: PERMISSION_CONSTANTS.EDIT_ACCESS.ALL,
        DeleteItems: PERMISSION_CONSTANTS.EDIT_ACCESS.ALL,
        ReadItems: PERMISSION_CONSTANTS.READ_ACCESS.FULL_DETAILS,
    },
    Editor: {
        PermissionLevel: PERMISSION_CONSTANTS.PERMISSION_LEVEL_TYPE.EDITOR,
        CanCreateItems: true,
        CanCreateSubFolders: false,
        IsFolderContact: false,
        IsFolderVisible: true,
        IsFolderOwner: false,
        EditItems: PERMISSION_CONSTANTS.EDIT_ACCESS.ALL,
        DeleteItems: PERMISSION_CONSTANTS.EDIT_ACCESS.ALL,
        ReadItems: PERMISSION_CONSTANTS.READ_ACCESS.FULL_DETAILS,
    },
    PublishingAuthor: {
        PermissionLevel: PERMISSION_CONSTANTS.PERMISSION_LEVEL_TYPE.PUBLISHING_AUTHOR,
        CanCreateItems: true,
        CanCreateSubFolders: true,
        IsFolderContact: false,
        IsFolderVisible: true,
        IsFolderOwner: false,
        EditItems: PERMISSION_CONSTANTS.EDIT_ACCESS.OWNED,
        DeleteItems: PERMISSION_CONSTANTS.EDIT_ACCESS.OWNED,
        ReadItems: PERMISSION_CONSTANTS.READ_ACCESS.FULL_DETAILS,
    },
    Author: {
        PermissionLevel: PERMISSION_CONSTANTS.PERMISSION_LEVEL_TYPE.AUTHOR,
        CanCreateItems: true,
        CanCreateSubFolders: false,
        IsFolderContact: false,
        IsFolderVisible: true,
        IsFolderOwner: false,
        EditItems: PERMISSION_CONSTANTS.EDIT_ACCESS.OWNED,
        DeleteItems: PERMISSION_CONSTANTS.EDIT_ACCESS.OWNED,
        ReadItems: PERMISSION_CONSTANTS.READ_ACCESS.FULL_DETAILS,
    },
    NoneditingAuthor: {
        PermissionLevel: PERMISSION_CONSTANTS.PERMISSION_LEVEL_TYPE.NONEDITING_AUTHOR,
        CanCreateItems: true,
        CanCreateSubFolders: false,
        IsFolderContact: false,
        IsFolderVisible: true,
        IsFolderOwner: false,
        EditItems: PERMISSION_CONSTANTS.EDIT_ACCESS.NONE,
        DeleteItems: PERMISSION_CONSTANTS.EDIT_ACCESS.OWNED,
        ReadItems: PERMISSION_CONSTANTS.READ_ACCESS.FULL_DETAILS,
    },
    Reviewer: {
        PermissionLevel: PERMISSION_CONSTANTS.PERMISSION_LEVEL_TYPE.REVIEWER,
        CanCreateItems: false,
        CanCreateSubFolders: false,
        IsFolderContact: false,
        IsFolderVisible: true,
        IsFolderOwner: false,
        EditItems: PERMISSION_CONSTANTS.EDIT_ACCESS.NONE,
        DeleteItems: PERMISSION_CONSTANTS.EDIT_ACCESS.NONE,
        ReadItems: PERMISSION_CONSTANTS.READ_ACCESS.FULL_DETAILS,
    },
    Contributor: {
        PermissionLevel: PERMISSION_CONSTANTS.PERMISSION_LEVEL_TYPE.CONTRIBUTOR,
        CanCreateItems: true,
        CanCreateSubFolders: false,
        IsFolderContact: false,
        IsFolderVisible: true,
        IsFolderOwner: false,
        EditItems: PERMISSION_CONSTANTS.EDIT_ACCESS.NONE,
        DeleteItems: PERMISSION_CONSTANTS.EDIT_ACCESS.NONE,
        ReadItems: PERMISSION_CONSTANTS.READ_ACCESS.NONE,
    },
    None: {
        PermissionLevel: PERMISSION_CONSTANTS.PERMISSION_LEVEL_TYPE.NONE,
        CanCreateItems: false,
        CanCreateSubFolders: false,
        IsFolderContact: false,
        IsFolderVisible: false,
        IsFolderOwner: false,
        EditItems: PERMISSION_CONSTANTS.EDIT_ACCESS.NONE,
        DeleteItems: PERMISSION_CONSTANTS.EDIT_ACCESS.NONE,
        ReadItems: PERMISSION_CONSTANTS.READ_ACCESS.NONE,
    },
    Custom: {
        PermissionLevel: PERMISSION_CONSTANTS.PERMISSION_LEVEL_TYPE.CUSTOM,
    },
};

/**
 * Function returns copy of permission object for a permission level.
 * Permission returned will not have UserId property as that is not dependent on permission level.
 * @param permissionLevel Permission level from PermissionLevelType
 * @returns Permission object
 */
export default function getPermissionFromPermissionLevel(
    permissionLevel: PermissionLevelType
): Permission {
    const permissionObj: Permission = permissionLevelToPermissionMap[permissionLevel];
    return { ...permissionObj };
}
